import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useProgressNavigation } from "@/components/progress-bar";
import { SiteMapItem, getAllCommandBarRoutes, getFilteredCommandBarRoutes } from "@/config/sitemap";
import { useTenant } from "@/contexts/tenant-provider";
import { trackKeyboardNavigation } from "@/lib/search-analytics";
import { PatientSummary } from "@/types/patient";
import { UserTiny } from "@/types/user";
import { useNavigationSearchOpenStore } from "@/stores/navigation-search-store";
import { useNavigationSidebarOpenStore } from "@/stores/navigation-sidebar-store";
export const SEARCH_MIN_LENGTH = 2;
export function buildUrl(path: string, tenant: {
  subDomain: string;
} | null | undefined) {
  return `/${tenant?.subDomain}${path}`;
}
export const getPageFromSitemap = (route: string): SiteMapItem | undefined => {
  return getAllCommandBarRoutes().find(page => page.route === route);
};
export const useKeyboardNavigation = (open, setOpen, totalRows, selectedRow, setSelectedRow, onSelect) => {
  const $ = _c(11);
  const {
    isCollapsed: isSidebarCollapsed,
    setIsCollapsed: setIsSidebarCollapsed
  } = useNavigationSidebarOpenStore();
  const {
    openSearch,
    closeSearch
  } = useNavigationSearchOpenStore();
  let t0;
  let t1;
  if ($[0] !== closeSearch || $[1] !== isSidebarCollapsed || $[2] !== onSelect || $[3] !== open || $[4] !== openSearch || $[5] !== selectedRow || $[6] !== setIsSidebarCollapsed || $[7] !== setSelectedRow || $[8] !== totalRows) {
    t0 = () => {
      const handleKeyDown = e => {
        if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          if (!open) {
            openSearch("keyboard_shortcut");
          } else {
            closeSearch("keyboard_shortcut");
          }
        } else {
          if (open) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              trackKeyboardNavigation("arrow_down");
              setSelectedRow(prev => prev === undefined ? 0 : prev >= totalRows - 1 ? 0 : prev + 1);
            } else {
              if (e.key === "ArrowUp") {
                e.preventDefault();
                trackKeyboardNavigation("arrow_up");
                setSelectedRow(prev_0 => prev_0 === undefined ? totalRows - 1 : prev_0 <= 0 ? totalRows - 1 : prev_0 - 1);
              } else {
                if (e.key === "Enter" && selectedRow !== undefined) {
                  e.preventDefault();
                  trackKeyboardNavigation("enter_select");
                  onSelect(selectedRow);
                } else {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    trackKeyboardNavigation("escape_close");
                    closeSearch("keyboard_shortcut");
                  }
                }
              }
            }
          }
        }
        if (e.key === "s" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          setIsSidebarCollapsed(!isSidebarCollapsed);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t1 = [open, openSearch, closeSearch, totalRows, selectedRow, setSelectedRow, onSelect, isSidebarCollapsed, setIsSidebarCollapsed];
    $[0] = closeSearch;
    $[1] = isSidebarCollapsed;
    $[2] = onSelect;
    $[3] = open;
    $[4] = openSearch;
    $[5] = selectedRow;
    $[6] = setIsSidebarCollapsed;
    $[7] = setSelectedRow;
    $[8] = totalRows;
    $[9] = t0;
    $[10] = t1;
  } else {
    t0 = $[9];
    t1 = $[10];
  }
  useEffect(t0, t1);
};
export const useOutsideClick = (ref, setSelectedRow, setOpen) => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== ref || $[1] !== setOpen || $[2] !== setSelectedRow) {
    t0 = () => {
      const handleClickOutside = event => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setSelectedRow(undefined);
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t1 = [ref, setSelectedRow, setOpen];
    $[0] = ref;
    $[1] = setOpen;
    $[2] = setSelectedRow;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
};
export const useSearchItems = (search, filteredPages, patients, recentItems) => {
  const $ = _c(29);
  const {
    tenant
  } = useTenant();
  let t0;
  if ($[0] !== tenant) {
    t0 = getFilteredCommandBarRoutes(tenant);
    $[0] = tenant;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const filteredRoutes = t0;
  let t1;
  if ($[2] !== filteredRoutes) {
    t1 = new Set(filteredRoutes.map(_temp));
    $[2] = filteredRoutes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const filteredRouteMap = t1;
  let t2;
  if ($[4] !== filteredRouteMap || $[5] !== recentItems.pages) {
    let t3;
    if ($[7] !== filteredRouteMap) {
      t3 = page => {
        if (!page) {
          return false;
        }
        return filteredRouteMap.has(page.route);
      };
      $[7] = filteredRouteMap;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = recentItems.pages.map(getPageFromSitemap).filter(t3).slice(0, 4);
    $[4] = filteredRouteMap;
    $[5] = recentItems.pages;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const recentPages = t2;
  let t3;
  bb0: {
    if (search.length >= SEARCH_MIN_LENGTH || recentPages.length >= 4) {
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t3 = t4;
      break bb0;
    }
    const remainingSlots = 4 - recentPages.length;
    let t4;
    if ($[10] !== recentPages) {
      t4 = new Set(recentPages.map(_temp2));
      $[10] = recentPages;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    const recentPageRoutes = t4;
    let t5;
    if ($[12] !== filteredRoutes || $[13] !== recentPageRoutes || $[14] !== remainingSlots) {
      let t6;
      if ($[16] !== recentPageRoutes) {
        t6 = page_1 => page_1.suggested && !recentPageRoutes.has(page_1.route);
        $[16] = recentPageRoutes;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      t5 = filteredRoutes.filter(t6).slice(0, remainingSlots);
      $[12] = filteredRoutes;
      $[13] = recentPageRoutes;
      $[14] = remainingSlots;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t3 = t5;
  }
  const suggestedPages = t3;
  let t4;
  bb1: {
    if (search.length < SEARCH_MIN_LENGTH) {
      let t5;
      if ($[18] !== recentItems.patients || $[19] !== recentPages || $[20] !== suggestedPages) {
        t5 = {
          pages: suggestedPages,
          recentPages,
          patients: recentItems.patients
        };
        $[18] = recentItems.patients;
        $[19] = recentPages;
        $[20] = suggestedPages;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      t4 = t5;
      break bb1;
    }
    let t5;
    if ($[22] !== filteredPages || $[23] !== patients) {
      t5 = {
        pages: filteredPages,
        patients
      };
      $[22] = filteredPages;
      $[23] = patients;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    t4 = t5;
  }
  const currentItems = t4;
  let t5;
  if ($[25] !== currentItems || $[26] !== recentPages || $[27] !== suggestedPages) {
    t5 = {
      suggestedPages,
      recentPages,
      currentItems
    };
    $[25] = currentItems;
    $[26] = recentPages;
    $[27] = suggestedPages;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
};
export function useSearchResultsCount(suggestedPages, pages, patients) {
  const $ = _c(5);
  const [totalRows, setTotalRows] = useState(0);
  let t0;
  let t1;
  if ($[0] !== pages.length || $[1] !== patients.length || $[2] !== suggestedPages.length) {
    t0 = () => {
      setTotalRows(suggestedPages.length + pages.length + patients.length);
    };
    t1 = [suggestedPages.length, pages.length, patients.length];
    $[0] = pages.length;
    $[1] = patients.length;
    $[2] = suggestedPages.length;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return totalRows;
}
export function useCommandBarSelection() {
  const $ = _c(5);
  const {
    tenant
  } = useTenant();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  let t0;
  if ($[0] !== navigateWithProgress) {
    t0 = (url, onSelect) => {
      navigateWithProgress(url);
      onSelect(url);
    };
    $[0] = navigateWithProgress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleSelect = t0;
  let t1;
  if ($[2] !== handleSelect || $[3] !== tenant) {
    t1 = {
      handleSelect: (url_0, onSelect_0) => handleSelect(buildUrl(url_0, tenant), onSelect_0)
    };
    $[2] = handleSelect;
    $[3] = tenant;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp(route) {
  return route.route;
}
function _temp2(page_0) {
  return page_0.route;
}